import React from "react";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, ClipboardList } from "lucide-react";

const OPTIONS = [["yes", "Yes, we can evidence this"], ["partially", "Partially / Inconsistently"], ["no", "No"], ["unsure", "Not sure"]];
const RAG = { green: "text-rag-green", amber: "text-amber-500", red: "text-rag-red" };

export function SelfCheckResult({ result, onExplore }) {
  return (
    <div className="py-2" data-testid="self-check-result">
      <div className="label-caps text-muted-foreground">Your assessment result</div>
      <div className="flex items-baseline gap-3 mt-2">
        <span data-testid="self-check-score" className={`text-4xl font-heading font-bold ${RAG[result.rag]}`}>{result.score}%</span>
        <h3 data-testid="self-check-result-title" className="text-lg font-heading font-bold text-brand leading-snug">{result.title}</h3>
      </div>
      <p className="text-sm text-muted-foreground mt-3">{result.summary}</p>
      <ul className="mt-3 space-y-3 rounded-xl border border-cream-deep bg-cream p-4" data-testid="self-check-areas">
        {result.areas.map((a) => (
          <li key={a.title} className="flex gap-3">
            <ClipboardList className="h-4 w-4 text-brand shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-brand">{a.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{a.hint}</div>
              <div className="text-xs text-coral mt-0.5">KOHC resource: {a.resource}</div>
            </div>
          </li>
        ))}
        {result.areas.length === 0 && <li className="text-sm text-brand">Every area is evidenced — keep auditing monthly to stay inspection-ready.</li>}
      </ul>
      <p className="font-heading font-bold text-brand mt-5 text-sm">Knowing where the gaps are is the first step. KOHC can help you address them.</p>
      <p className="text-sm text-muted-foreground mt-1">Explore our oral healthcare policies, practical resources, staff training and audit tools to help strengthen your evidence and everyday practice.</p>
      <Button data-testid="self-check-to-trial" onClick={onExplore} className="mt-4 w-full bg-coral hover:bg-coral-hover text-white rounded-xl h-12 text-base font-semibold">Explore How KOHC Can Help <ArrowRight className="h-4 w-4 ml-2" /></Button>
      <p className="text-xs text-muted-foreground text-center mt-3">We've emailed this summary to you too.</p>
    </div>
  );
}

export function SelfCheckLeadForm({ form, setForm, loading, onSubmit, onBack }) {
  const field = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-brand text-2xl font-heading">Almost there — where should we send your score?</DialogTitle>
        <DialogDescription>Your result and tailored next steps will appear here and land in your inbox.</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 mt-2">
        <div><Label>Your name</Label><Input data-testid="self-check-name" required value={form.name} onChange={field("name")} /></div>
        <div><Label>Work email</Label><Input data-testid="self-check-email" type="email" required value={form.email} onChange={field("email")} /></div>
        <div><Label>Care home / service name</Label><Input data-testid="self-check-home" required value={form.care_home_name} onChange={field("care_home_name")} /></div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onBack} className="rounded-full h-11"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          <Button data-testid="self-check-submit" type="submit" disabled={loading} className="flex-1 bg-coral hover:bg-coral-hover text-white rounded-full h-11">{loading ? "Scoring…" : "Show my score"}</Button>
        </div>
      </form>
    </>
  );
}

export function SelfCheckQuestion({ questions, step, onAnswer, onBack }) {
  const q = questions[step];
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-brand text-2xl font-heading">Free Oral Healthcare Readiness Assessment</DialogTitle>
        <DialogDescription>{questions.length ? `Question ${step + 1} of ${questions.length} · about 3 minutes` : "Loading…"}</DialogDescription>
      </DialogHeader>
      {q && (
        <div className="mt-2">
          <div className="h-1.5 bg-cream-deep rounded-full overflow-hidden mb-5"><div className="h-full bg-coral rounded-full transition-[width] duration-300" style={{ width: `${(step / questions.length) * 100}%` }} /></div>
          <div className="label-caps text-coral mb-2">{q.cat}</div>
          <p data-testid="self-check-question" className="text-base md:text-lg font-heading font-semibold text-brand min-h-[4.5rem]">{q.q}</p>
          <div className="grid grid-cols-2 gap-2 mt-5">
            {OPTIONS.map(([v, label]) => (
              <Button key={v} type="button" variant="outline" data-testid={`self-check-answer-${v}`} onClick={() => onAnswer(v)} className="h-12 rounded-xl border-brand/30 text-brand hover:bg-brand hover:text-white text-sm whitespace-normal">{label}</Button>
            ))}
          </div>
          {step > 0 && <button type="button" onClick={onBack} className="mt-4 text-sm text-muted-foreground hover:text-brand inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" />Back</button>}
        </div>
      )}
    </>
  );
}
